import React, { useState, useEffect } from 'react';
import { 
  AppBar, Toolbar, Button, InputBase, IconButton, Badge, Grid, Box, 
  MenuItem, Menu, keyframes, Drawer, List, ListItem, ListItemText,
  Divider, useMediaQuery, SwipeableDrawer, Typography, styled 
} from '@mui/material';
import { FavoriteBorderOutlined, Search, Close } from '@mui/icons-material';
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import PhoneIcon from '@mui/icons-material/Phone';
import ReferralDialog from '../authentication/RefferalDialog';
import { useNavigate } from "react-router-dom";
import logo from "../images/logo.png";
import { useTheme } from '@mui/material';
const marquee = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(-100%); }
`;

// Your original styled components
const SearchContainer = styled('div')({
  position: 'relative',
  borderRadius: '100px',
  backgroundColor: '#fff',
  width: '100%',
  maxWidth: '100%',
  '&:hover': {
    backgroundColor: '#ffffff',
    boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.15)',
  },
});

const SearchIconWrapper = styled('div')({
  position: 'absolute',
  left: '0',
  top: '0',
  width: '50px',
  height: '100%',
  backgroundColor: '#1E7CB7',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: '#fff',
  borderRadius: "100px 0px 0px 100px"
});

const StyledInputBase = styled(InputBase)(() => ({
  paddingLeft: '75px',
  paddingRight: '20px',
  paddingTop: '8px',
  paddingBottom: '8px',
  width: '100%',
  fontSize: '14px',
  color: '#333',
  border: 'none',
  outline: 'none',
  transition: 'all 0.3s ease',
  '&:focus': {
    backgroundColor: '#ffffff',
  },
  '&::placeholder': {
    color: '#aaa',
    fontSize: '14px',
  },
}));

const menuItemStyles = {
  fontSize: "14px",
  fontWeight: 500,
  padding: "10px 20px",
  letterSpacing: "0.5px",
  transition: "background 0.3s ease",
  "&:hover": {
    backgroundColor: "#f5f5f5",
    color: "#000",
  },
};

function Navbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  // Your original useEffect hooks
  useEffect(() => {
    const sessionUser = JSON.parse(sessionStorage.getItem("user"));
    setUser(sessionUser);
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      const cart = JSON.parse(sessionStorage.getItem('cart')) || [];
      const count = cart.reduce((sum, item) => sum + (item.quantity || 0), 0);
      setCartCount(count);
    }

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cartUpdated', handleStorageChange);
    handleStorageChange();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cartUpdated', handleStorageChange);
    };
  }, []);

  // Mobile handlers
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleCategoriesToggle = () => setCategoriesOpen(!categoriesOpen);

  // Your original handlers
  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);
  const handleLogout = () => {
    sessionStorage.removeItem("user");
    setUser(null);
    navigate("/");
  };

  // Mobile menu content
  const mobileMenuContent = (
    <Box sx={{ width: 250 }} role="presentation">
      <List>
        <ListItem button onClick={handleCategoriesToggle}>
          <ListItemText primary="Shop by Categories" />
        </ListItem>
        {['Home', 'About Us', 'Shop', 'Our Team', 'Contact'].map((text, index) => (
          <ListItem button key={text} onClick={() => {
            navigate(text.toLowerCase().replace(' ', '-'));
            handleDrawerToggle();
          }}>
            <ListItemText primary={text} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // Categories drawer content
  const categoriesContent = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Categories</Typography>
        <IconButton onClick={handleCategoriesToggle}>
          <Close />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {['Electronics', 'Fashion', 'Home & Kitchen', 'Beauty', 'Sports'].map((category) => (
          <ListItem button key={category} onClick={handleCategoriesToggle}>
            <ListItemText primary={category} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      {/* Marquee Banner */}
      <Box sx={{
        height: '30px',
        backgroundColor: 'rgb(52, 52, 52)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        zIndex: 1300,
        boxShadow: 1,
        display: { xs: 'none', md: 'flex' }
      }}>
        <Box sx={{
          animation: `${marquee} 30.688s linear infinite`,
          whiteSpace: 'nowrap',
          fontWeight: 'bold',
          fontSize: '0.875rem',
        }}>
          ! FLASH SALE ! &nbsp;&nbsp; &nbsp;&nbsp;&nbsp;&nbsp;<i>50% off all items - limited time offer - Use Code: FLASH50</i> &nbsp;&nbsp; &nbsp;&nbsp;&nbsp;&nbsp;! FLASH SALE !
        </Box>
      </Box>

      {/* Main Navbar */}
      <AppBar position="static" sx={{ backgroundColor: "rgba(137, 184, 68, 0.1)", boxShadow: "0" }}>
        <Toolbar>
          <Grid container alignItems="center">
            {/* Logo Section */}
            <Grid item xs={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isMobile && (
                  <IconButton
                    color="black"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ mr: 1 }}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
                <img 
                  src={logo} 
                  alt="Logo" 
                  style={{ 
                    width: isMobile ? '50%' : '15%',
                    cursor: "pointer",
                    minWidth: isMobile ? "40px" : "120px" ,
                  }} 
                  onClick={() => navigate("/")} 
                />
              </Box>
            </Grid>

            {/* Search Bar */}
            <Grid item md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <SearchContainer>
                <SearchIconWrapper>
                  <Search />
                </SearchIconWrapper>
                <StyledInputBase placeholder="Search products, categories, or brands..." />
              </SearchContainer>
            </Grid>

            {/* Account and Cart Icons */}
            <Grid item xs={6} md={3} container justifyContent="flex-end">
              <Box sx={{ display: 'flex', gap: "20px", alignItems: 'center' }}>
             

                {/* Account Dropdown */}
                <div onMouseEnter={!isMobile ? handleOpen : undefined} 
                     onClick={isMobile ? handleOpen : undefined}>
                  <Button
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#2A3B5A',
                      textTransform: 'none',
                      p: '8px 12px',
                      '&:hover': { backgroundColor: '#f5f7fa' },
                    }}
                  >
                    {!user ? (
                      <div style={{ textAlign: 'left' }}>
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1.2, color: '#6b778c', fontWeight: "900" }}>
                          Welcome
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2, fontWeight: 600 }}>
                          Login & Sign In
                        </Typography>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'left' }}>
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1.2, color: '#6b778c', fontWeight: "900" }}>
                          Welcome
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2, fontWeight: 600 }}>
                          Go to Dashboard
                        </Typography>
                      </div>
                    )}
                    <KeyboardArrowDownIcon sx={{ fontSize: '20px', color: '#2A3B5A' }} />
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    MenuListProps={{ onMouseLeave: handleClose }}
                    sx={{
                      '& .MuiPaper-root': {
                        borderRadius: '8px',
                        minWidth: '180px',
                        backgroundColor: '#fff',
                        color: '#333',
                        boxShadow: '0px 4px 10px rgba(0,0,0,0.2)',
                        mt: 1,
                      },
                    }}
                  >
                    {user ? (
                      <>
                        <MenuItem onClick={() => navigate('/dashboard')} sx={menuItemStyles}>
                          Dashboard
                        </MenuItem>
                        <MenuItem onClick={() => navigate('/profile')} sx={menuItemStyles}>
                          Profile
                        </MenuItem>
                        <MenuItem onClick={handleLogout} sx={menuItemStyles}>
                          Logout
                        </MenuItem>
                      </>
                    ) : (
                      <>
                        <MenuItem onClick={handleOpenDialog} sx={menuItemStyles}>
                          Register
                        </MenuItem>
                        <MenuItem onClick={() => navigate('/login')} sx={menuItemStyles}>
                          Login
                        </MenuItem>
                      </>
                    )}
                  </Menu>
                </div>

                {/* Cart Icons */}
                <IconButton onClick={() => navigate('/cart')} sx={{ 
                  width: 35, 
                  height: 35, 
                  borderRadius: "50%", 
                  backgroundColor: "#1E7CB7",
                  '&:hover': { backgroundColor: "#005a8d" } 
                }}>
                  <Badge color="error">
                    <FavoriteBorderOutlined sx={{ color: "#fff" }} />
                  </Badge>
                </IconButton>
                <IconButton onClick={() => navigate('/cart')} sx={{ 
                  width: 35, 
                  height: 35, 
                  borderRadius: "50%", 
                  backgroundColor: "#1E7CB7",
                  '&:hover': { backgroundColor: "#005a8d" } 
                }}>
                  <Badge badgeContent={cartCount} color="error">
                    <ShoppingBagOutlinedIcon sx={{ color: "#fff" }} />
                  </Badge>
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>

      {/* Secondary Navigation */}
      <Box sx={{ 
        backgroundColor: "#2FA8EF", 
        color: "#fff", 
        p: "0px 30px", 
        display: { xs: 'none', md: 'flex' }, 
        justifyContent: "space-between", 
        alignItems: "center", 
        fontSize: "14px", 
        gap: "10px", 
        borderRadius: "0px" 
      }}>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            onClick={handleCategoriesToggle}
            sx={{
              color: "#fff",
              fontSize: "16px",
              fontWeight: "600",
              textTransform: "none",
              background: "#1E7CB7",
              padding: "8px 18px",
              // borderRadius: "5px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                // backgroundColor: "#2FA8EF",
                transform: "scale(1.05)",
                border:"1px solid #fff"
              },
            }}
          >
            <IconButton
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  backgroundColor: "#2FA8EF",
                },
              }}
            >
              <MenuIcon sx={{ color: "#fff", fontSize: 20 }} />
            </IconButton>
            Shop by categories
          </Button>

          {['Home', 'About Us', 'Shop', 'Our Team', 'Contact'].map((item) => {
  const path = item === 'Home' ? '/' : `/${item.toLowerCase().replace(/\s+/g, '-')}`;
  
  return (
    <Button
      key={item}
      onClick={() => navigate(path)}
      sx={{ 
        color: "#fff", 
        fontSize: "14px", 
        fontWeight: "600", 
        textTransform: "none" 
      }}
    >
      {item}
    </Button>
  );
})}

        </Box>

        <Button sx={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "8px",
          color: "#fff",
          fontSize: "14px",
          fontWeight: "600",
          textTransform: "none",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.1)",
          }
        }}>
          <PhoneIcon sx={{ fontSize: 30 }} />
          +977 1-5918717
        </Button>
      </Box>

      {/* Mobile Components */}
      <SwipeableDrawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        onOpen={handleDrawerToggle}
      >
        {mobileMenuContent}
      </SwipeableDrawer>

      <Drawer
        anchor="left"
        open={categoriesOpen}
        onClose={handleCategoriesToggle}
      >
        {categoriesContent}
      </Drawer>

      {/* Mobile Search Bar */}
      {isMobile && (
        <Box sx={{ px: 2, py: 1 }}>
          <SearchContainer>
            <SearchIconWrapper>
              <Search />
            </SearchIconWrapper>
            <StyledInputBase placeholder="Search products..." />
          </SearchContainer>
        </Box>
      )}

      <ReferralDialog open={openDialog} handleClose={handleCloseDialog} />
    </>
  );
}

export default Navbar;